// @ts-nocheck
import { Container } from "components/layout/Container";
import { ContentsTable, ContentsTableItem } from "components/layout/ContentsTable";
import { ArticleAuthorCard } from "components/pages/blog/article/ArticleAuthorCard";
import styles from "./ArticleContent.module.scss";

import { ArticleDate } from "./ArticleDate";
// import { WrappedIcon } from "components/UI/WrappedIcon";
import { useArticleSinglePageContext } from "@/helpers/contexts/ArticleSinglePageContext";
import { StrapiArticleSectionComponentData } from "@/types/strapi/components/article/section";
import { useMemo } from "react";
import { ArticleDynamicZoneContentItem } from "@/types/strapi/article";
import { EditorContentData } from "@/components/pages/article/EditorContent/types";
import { StrapiLegacySampleCardDynamicZoneItem } from "@/types/strapi/components/shared/legacySampleCard";
import { StrapiSampleCardDynamicZoneItem } from "@/types/strapi/components/shared/sampleCard";
import { StrapiCTABlockDynamicZoneItem } from "@/types/strapi/components/article/ctaBlock";
import dynamic from "next/dynamic";
import { WrappedIcon } from "@/components/UI/WrappedIcon";
import { ArticleCTACard } from "@/components/pages/blog/article/ArticleCTACard";
import { ArticleLikeDislike } from "@/components/pages/blog/article/ArticleLikeDislike";
import cn from "classnames";
import cs from "@/styles/modules/card.module.scss";
import { ShareButton } from "@/components/UI/Buttons/ShareButton";

const Comments = dynamic(() => import("components/shared/comments/Comments"));
const EditorContent = dynamic(() => import("../../article/EditorContent"));
const ArticleFaq = dynamic(() => import("./ArticleFaq"));
const ContentCTA = dynamic(() => import("../../../typography/ContentCTA"));
const ArticleSampleCard = dynamic(() => import("./ArticleSampleCard"));
const LegacyContentSampleCard = dynamic(() => import("../../samples/LegacyContentSampleCard"));

type ArticleContentProps = {
  showComments?: boolean;
  showShareBtn?: boolean;
  showLikes?: boolean;
};

export const TArticleContent = ({
  showComments = true,
  showLikes = true,
  showShareBtn = true,
}: ArticleContentProps) => {
  const contextData = useArticleSinglePageContext();

  const articleContent = contextData?.article?.text;
  const articleFaq = contextData?.article?.faq;

  const author = contextData?.article?.author;

  const tableContents: ContentsTableItem[] = useMemo(() => {
    if (!articleContent) return [];

    const value =
      (articleContent
        .map(({ id, __component, ...rest }) => {
          const { heading, isTableOfContent } = rest as StrapiArticleSectionComponentData;
          if (__component === "article.section" && isTableOfContent) {
            return {
              id: `section-${id}`,
              heading,
            };
          }
          return false;
        })
        .filter(Boolean) as ContentsTableItem[]) || [];

    if (articleFaq && !!articleFaq?.list.length) {
      value.push({
        id: `faq-section`,
        heading: "Frequently Asked Questions",
      });
    }
    return value;
  }, [articleContent, articleFaq]);

  if (!contextData) return null;
  const { pageId, article } = contextData;
  const { slug, textAfterHeading, content, faq, publishedAt, updatedAt, comments } = article;

  const renderContent = ({ id, __component, ...rest }: ArticleDynamicZoneContentItem) => {
    if (__component === "article.section") {
      const { content } = rest as StrapiArticleSectionComponentData;
      let parsedContent: EditorContentData | undefined;
      try {
        parsedContent = JSON.parse(content);
      } catch (err) {}
      if (!parsedContent) return null;
      return (
        <div key={`${__component}-${id}`}>
          <div id={`section-${id}`}>
            <EditorContent data={parsedContent} slug={slug} />
          </div>
        </div>
      );
    }
    if (__component === "shared.sample-card") {
      const { heading, pages, subject, format, level, paperType, file } =
        rest as StrapiLegacySampleCardDynamicZoneItem;
      return (
        <div key={`${__component}-${id}`} className={styles.sampleCard}>
          <LegacyContentSampleCard
            heading={heading}
            pages={pages}
            subject={subject}
            format={format}
            level={level}
            paperType={paperType}
            fileData={file}
          />
        </div>
      );
    }
    if (__component === "shared.sample-card-v2") {
      return (
        <div key={`${__component}-${id}`} className={styles.sampleCard}>
          <ArticleSampleCard {...(rest as StrapiSampleCardDynamicZoneItem)} />
        </div>
      );
    }
    if (__component === "article.cta-block") {
      const { heading, content } = rest as StrapiCTABlockDynamicZoneItem;
      let parsedContent: EditorContentData | undefined;
      try {
        parsedContent = JSON.parse(content);
      } catch (err) {}
      if (!parsedContent) return null;
      return (
        <ContentCTA heading={heading} key={`${__component}-${id}`}>
          <EditorContent data={parsedContent} slug={slug} />
        </ContentCTA>
      );
    }
  };

  return (
    <Container>
      <div className={styles.wrapper}>
        <div className={styles.contentsTableWrap}>
          <ContentsTable key={slug} shrinkHeight={false} items={tableContents} />
          <ArticleCTACard className={styles.ctaCard} />
        </div>
        <div className={styles.main}>
          <section className={styles.content}>
            {textAfterHeading && <EditorContent data={textAfterHeading} slug={slug} />}
            {content && content.map((contentBlock) => renderContent(contentBlock))}
          </section>

          {faq && !!faq?.list.length && <ArticleFaq title={faq.heading} list={faq.list} />}

          {showLikes && <ArticleLikeDislike slug={slug} />}

          <div className={styles.bottomDivider} />

          <div className={cn(cs.cardGlass, styles.bottomInfo)}>
            <div className={styles.dates}>
              <div className={styles.date}>
                Article posted on:
                <span>
                  <ArticleDate date={publishedAt} />
                </span>
              </div>
              {updatedAt && (
                <div className={styles.date}>
                  Article updated on:
                  <span>
                    <ArticleDate date={updatedAt} />
                  </span>
                </div>
              )}
            </div>
            {showShareBtn && <ShareButton />}
          </div>

          {author && <ArticleAuthorCard />}
          {showComments && <Comments pageId={pageId} pageUrl={slug} list={comments || []} />}
        </div>
      </div>
    </Container>
  );
};
